import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiBaseUrls, ApiControllers } from '../constants/constants';
import { CountryModel } from '../models/data/country.model';
import { TimezoneModel } from '../models/data/timezone.model';
import { PaginatedListModel } from '../models/service/paginated-list.model';
import { PaginationResponseHeader } from '../models/service/pagination-response-header.model';
import { CountriesFilter } from '../filters/countries.filter';
import { StatesFilter } from '../filters/states.filter';
import { StateProvinceModel } from '../models/data/state-province.model';
import { APISanitizerUtilities } from '../utilities/api-sanitizer.utilities';
import { ServiceResultModel } from '../models/service/service-result.model';
import { ServiceUtilities } from '../utilities/service.utilities';

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private apiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.LOCATIONS()}`;

  constructor(private httpClient: HttpClient) { }

  public async searchCountries(countriesFilter: CountriesFilter, orderBy?: string, skip?: number, take?: number): Promise<ServiceResultModel<PaginatedListModel<CountryModel>>> {
    try {
      orderBy = (orderBy) && encodeURIComponent(orderBy);
      const countriesPath = "/countries"
      const requestUrl = `${this.apiPath}${countriesPath}/filter?orderBy=${orderBy}&skip=${skip}&take=${take}`;

      const response = await firstValueFrom(
        this.httpClient.post<CountryModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<CountriesFilter>(countriesFilter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<CountryModel> = {
        data: <CountryModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async searchStateProvinces(statesFilter: StatesFilter, orderBy?: string, skip?: number, take?: number): Promise<ServiceResultModel<PaginatedListModel<StateProvinceModel>>> {
    try {
      orderBy = (orderBy) && encodeURIComponent(orderBy);
      const statesPath = "/states"
      const requestUrl = `${this.apiPath}${statesPath}/filter?orderBy=${orderBy}&skip=${skip}&take=${take}`;

      const response = await firstValueFrom(
        this.httpClient.post<StateProvinceModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<StatesFilter>(statesFilter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<StateProvinceModel> = {
        data: <StateProvinceModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getTimezones(): Promise<ServiceResultModel<TimezoneModel[]>> {
    try {
      const timezonesPath = "/timezones";
      const requestUrl = `${this.apiPath}${timezonesPath}`;

      const response = await firstValueFrom(
        this.httpClient.get<TimezoneModel[]>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      return <ServiceResultModel<TimezoneModel[]>>{ 
        data: <TimezoneModel[]>response.body?.valueOf(), 
        success: true, 
        statusCode: response.status 
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }
}
